import type React from "react";
import { Modal, Dialog as RACDialog } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import type { DialogFooterConfig, DialogHeaderConfig } from "../Dialog/types";
import { DialogFooter } from "../DialogFooter";
import { DialogHeader } from "../DialogHeader";
import { ErrorBoundary } from "../ErrorBoundary";
import { ModalBackdrop } from "../ModalBackdrop";

export interface DrawerProps extends DialogHeaderConfig, DialogFooterConfig {
  /**
   * Drawer content
   */
  children?: React.ReactNode;
  /**
   * Whether the drawer is open
   */
  isOpen: boolean;
  /**
   * Side to slide in from
   * @default "right"
   */
  slideInFrom?: "left" | "right";
  /**
   * Whether to use a transparent backdrop
   * @default false
   */
  transparentOverlay?: boolean;
  /**
   * Callback when the drawer is closed
   */
  onClose?: () => void;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether to include padding inside the content area
   * @default true
   */
  contentPadding?: boolean;
  /**
   * Maximum width of the drawer
   * @default "400px"
   */
  maxWidth?: string;
}

/**
 * Drawer
 *
 * Sliding panel that anchors to screen edges.
 */
export function Drawer({
  children,
  title,
  headerContent,
  titleAlign = "left",
  hideCloseIcon = false,
  hasBackArrow = false,
  onBack,
  isOpen,
  slideInFrom = "right",
  transparentOverlay = false,
  onClose,
  className,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  footerContent,
  contentPadding = true,
  maxWidth = "400px",
}: DrawerProps) {
  if (!isOpen) return null;

  const hasHeader = title || headerContent;
  const hasFooter = primaryAction || secondaryAction || tertiaryAction || footerContent;

  return (
    <ErrorBoundary>
      <ModalBackdrop
        isOpen={isOpen}
        onOpenChange={(open) => !open && onClose?.()}
        isDismissable
        transparent={transparentOverlay}
      >
        <Modal className="outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0">
          <RACDialog
            className={twMerge(
              "fixed top-0 bottom-0 flex flex-col bg-background-surface shadow-xl overflow-hidden",
              slideInFrom === "right" ? "right-0" : "left-0",
              slideInFrom === "right"
                ? "animate-in slide-in-from-right duration-300 ease-out"
                : "animate-in slide-in-from-left duration-300 ease-out",
              "data-[exiting]:animate-out",
              slideInFrom === "right" ? "data-[exiting]:slide-out-to-right" : "data-[exiting]:slide-out-to-left",
              "data-[exiting]:duration-200 data-[exiting]:ease-in",
              "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-action-default focus-visible:outline-offset-0",
              className
            )}
            style={{
              maxWidth: maxWidth,
              width: "100%",
            }}
          >
            {hasHeader &&
              (hasBackArrow && onBack ? (
                <DialogHeader
                  title={title}
                  headerContent={headerContent}
                  titleAlign={titleAlign}
                  hideCloseIcon={hideCloseIcon}
                  hasBackArrow={true}
                  onBack={onBack}
                  onClose={onClose}
                />
              ) : (
                <DialogHeader
                  title={title}
                  headerContent={headerContent}
                  titleAlign={titleAlign}
                  hideCloseIcon={hideCloseIcon}
                  onClose={onClose}
                />
              ))}
            <div className={twMerge("flex-1 overflow-y-auto overscroll-contain", contentPadding ? "px-6 py-6" : "p-0")}>
              {children}
            </div>
            {hasFooter && (
              <DialogFooter
                primaryAction={primaryAction}
                secondaryAction={secondaryAction}
                tertiaryAction={tertiaryAction}
                footerContent={footerContent}
              />
            )}
          </RACDialog>
        </Modal>
      </ModalBackdrop>
    </ErrorBoundary>
  );
}

Drawer.displayName = "Drawer";
// Export types
